/**
 * AddSlides operation schemas owned by orchestrator runtime.
 *
 * These schemas are intentionally local to the orchestrator addSlides tool so
 * it does not depend on the removed addSlides agent wrapper package.
 */

import { z } from "zod";

/**
 * Source slide reference - can be 1-based position or slide ID string.
 * Used to copy layout from an existing slide.
 */
export const SourceSlideRefSchema = z.union([
    z.number().int().min(1),
    z.string().regex(/^slide_\d+$/),
]).describe("Source slide to copy layout from. Use EITHER: (1) 1-based position as number (e.g., 5 = fifth slide) OR (2) SlideID string format 'slide_N' (e.g., 'slide_256').");

/**
 * Add Empty operation.
 *
 * Layout selection approaches are mutually exclusive:
 * - layoutId (+ optional layoutName)
 * - sourceSlideRef
 */
export const AddEmptyOperationSchema = z.object({
    position: z.number().int().min(1)
        .describe("1-based slide position for insertion (e.g., position 1 = before first slide, position 5 = before slide 5)"),
    layoutId: z.string().optional()
        .describe("Layout ID from style guide (e.g., 'layout_2147483951#0'). Use this OR sourceSlideRef, not both."),
    layoutName: z.string().optional()
        .describe("Optional layout name for name-based matching (e.g., 'Title Slide'). Only used with layoutId approach."),
    sourceSlideRef: SourceSlideRefSchema.optional()
        .describe("Copy layout from an existing slide. Use 1-based position (e.g., 5) or slide ID (e.g., 'slide_256'). Use this OR layoutId, not both."),
}).refine(
    (data) => {
        const hasLayoutApproach = data.layoutId !== undefined;
        const hasSourceSlideApproach = data.sourceSlideRef !== undefined;
        return hasLayoutApproach !== hasSourceSlideApproach;
    },
    {
        message: "Must provide exactly one layout approach: layoutId (with optional layoutName) OR sourceSlideRef, not both or neither.",
    }
);

/**
 * Options for clearing content after copying a slide.
 */
export const ClearOptionsSchema = z.object({
    clearTextContent: z.boolean().default(false)
        .describe("Clear text from content shapes (AutoShapes, TextBoxes). Does NOT affect layout placeholders."),
    resetPlaceholders: z.boolean().default(false)
        .describe("Reset layout placeholders to 'Click to add text' prompt state. Rarely needed."),
    clearTableContent: z.boolean().default(false)
        .describe("Empty table cell contents while preserving table structure"),
    clearChartData: z.boolean().default(false)
        .describe("Delete chart shapes entirely"),
    removeNonPlaceholderShapes: z.boolean().default(false)
        .describe("Delete non-placeholder shapes entirely (images, icons, decorations)"),
});

/**
 * Copy Slide operation.
 */
export const CopySlideOperationSchema = z.object({
    position: z.number().int().min(1)
        .describe("1-based slide position for insertion (e.g., position 1 = before first slide)"),
    sourceSlideId: z.union([
        z.number().int().min(1),
        z.string().regex(/^slide_\d+$/),
    ]).describe("Source slide to copy. Use EITHER: (1) 1-based position as number (e.g., 5 = fifth slide) OR (2) SlideID string format 'slide_N' (e.g., 'slide_256'). Prefer using the position number."),
    clearOptions: ClearOptionsSchema.optional()
        .describe("Options for clearing content after copy. If omitted, creates exact duplicate."),
});

export type AddEmptyOperation = z.infer<typeof AddEmptyOperationSchema>;
export type CopySlideOperation = z.infer<typeof CopySlideOperationSchema>;
export type ClearOptions = z.infer<typeof ClearOptionsSchema>;
export type SourceSlideRef = z.infer<typeof SourceSlideRefSchema>;
